import {FC, useCallback, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Button from 'components/Button';
import Icon from 'components/Icon';
import SortableContainer from 'components/Sortable/SortableContainer';
import SortableHandle from 'components/Sortable/SortableHandle';
import SortableItem from 'components/Sortable/SortableItem';
import TooltipWrapper from 'components/Tooltip';
import {twMerge} from 'tailwind-merge';
import FilenamePreview from '../../common/FilenamePreview';
import PdfFilenameComponentName from '../../common/PdfFilenameComponentName';
import PdfFilenameSectionHeader from '../../common/SectionHeader';
import {PdfFilenameSettingsFormFields} from '../../services';
import {PdfFilenameElementOption, PdfFilenameSectionName} from '../../types';
import AddItemModal from '../AddItemModal';

type SectionProps = {
    sectionName: PdfFilenameSectionName;
};

const Section: FC<SectionProps> = ({sectionName}) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const {t} = useTranslation('Document.PdfFilenameSettings');

    const {setValue, watch} = useFormContext<PdfFilenameSettingsFormFields>();
    const elements = watch(sectionName);

    const items = elements.map((element) => ({id: element}));
    const numberItems = items.length;
    const canDelete = numberItems > 1;

    const onItemRemove = useCallback(
        (index: number) => {
            const newItems = [...items];
            newItems.splice(index, 1);
            setValue(
                sectionName,
                newItems.map(({id}) => id),
            );
        },
        [items, sectionName, setValue],
    );

    const [hoverId, setHoverId] = useState<PdfFilenameElementOption | null>(
        null,
    );

    return (
        <div
            className="mb-4 flex flex-col gap-y-3"
            data-testid={`section:${sectionName}`}
        >
            <PdfFilenameSectionHeader sectionName={sectionName} />
            <div>
                <FilenamePreview
                    elements={elements}
                    hoverId={hoverId}
                    sectionName={sectionName}
                />
            </div>
            <div className="overflow-hidden rounded border border-grey-300">
                <div>
                    <div className="flex flex-row items-start border-b border-grey-300 bg-grey-200">
                        <div className="w-20 p-2 pl-4">{t('table.order')}</div>
                        <div className="flex-1 p-2 pl-4">
                            {t('table.component')}
                        </div>
                    </div>
                </div>
                <SortableContainer
                    items={items}
                    onSortChange={(newItems) =>
                        setValue(
                            sectionName,
                            newItems.map(({id}) => id),
                        )
                    }
                    renderItem={({id}, index) => (
                        <SortableItem
                            key={id}
                            className={twMerge(
                                'flex border-grey-300 border-b',
                                index === numberItems - 1 &&
                                    'border-b-transparent',
                            )}
                            id={id}
                            onMouseOut={() => setHoverId(null)}
                            onMouseOver={() => setHoverId(id)}
                        >
                            <SortableHandle className="w-20 p-2 pl-4" id={id}>
                                <Icon
                                    className="py-1"
                                    icon="slider_handle"
                                    size="base"
                                />
                            </SortableHandle>
                            <div
                                className="flex-1 p-2 pl-4"
                                role="presentation"
                            >
                                <PdfFilenameComponentName element={id} />
                            </div>
                            <TooltipWrapper
                                message={canDelete ? '' : t('message.noItem')}
                            >
                                <Button
                                    className="p-2"
                                    disabled={!canDelete}
                                    icon="trash"
                                    id={`${sectionName}:delete-${id}`}
                                    kind="tertiary"
                                    onClick={() => onItemRemove(index)}
                                    size="sm"
                                />
                            </TooltipWrapper>
                        </SortableItem>
                    )}
                />
            </div>
            <div className="flex gap-x-2">
                <Button
                    icon="add"
                    id={`${sectionName}:add`}
                    kind="secondary"
                    onClick={() => setModalOpen(true)}
                    size="sm"
                >
                    {t('button.selectComponents')}
                </Button>
                <AddItemModal
                    isOpen={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    sectionName={sectionName}
                />
            </div>
        </div>
    );
};

export default Section;
